import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import {
  Card, CardContent, Typography, Stack, Chip, Box, Alert,
  FormControlLabel, Checkbox, TextField, MenuItem, Avatar,
} from "@mui/material";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { username as usernameDe } from "../lib/identity";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";

interface MeResponse {
  sub: string; preferred_username: string; tenant_id: string;
  tenants: string[]; groups: string[]; is_internal: boolean;
  email?: string; phone?: string;
}

function formatarTelefone(digitos?: string): string {
  if (!digitos) return "";
  const m = digitos.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${digitos}`;
}

function initials(s?: string): string {
  if (!s) return "??";
  const parts = s.split(/[@\s._-]/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : s.slice(0, 2)).toUpperCase();
}

export function MyProfile() {
  const auth = useAuth();
  const { tenants, isInternal } = useTenant();
  const api = useApi();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.call<MeResponse>("/api/me", { tenant: null }) });

  const p = auth.user?.profile as any;
  const email = p?.email || "—";
  const firstName = p?.given_name || p?.name || "";
  const lastName = p?.family_name || "";
  // no Cognito o username e um UUID interno: nunca exibir; o login e o email
  const username = p?.email || usernameDe(p);
  const nome = `${firstName} ${lastName}`.trim() || username;
  const telefone = formatarTelefone(me.data?.phone);
  const expTs = auth.user?.expires_at ? new Date(auth.user.expires_at * 1000) : null;

  return (
    <>
      {me.error && <AvisoDeLeitura oQue="os seus dados de acesso" />}
      <PageHeader eyebrow="Conta" title="Meu perfil" subtitle="Seus dados de acesso" />

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: 22 }}>{initials(nome)}</Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ color: "primary.dark" }}>
                {nome}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="perfil-email">{email}</Typography>
              <Typography variant="body2" color="text.secondary" data-testid="perfil-telefone">
                {telefone ? `WhatsApp ${telefone}` : "Sem telefone cadastrado — a Sophia no WhatsApp não reconhece você"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                {isInternal && (
                  <Chip
                    size="small"
                    icon={<ShieldRoundedIcon sx={{ fontSize: 14 }} />}
                    label="admin interno"
                    color="warning"
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  Sessão expira {expTs ? expTs.toLocaleTimeString() : "—"}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.dark" }}>Acesso a tenants</Typography>
          {tenants.length === 0 ? (
            <Alert severity="warning">Sem tenants atribuídos. Fale com o admin do seu tenant.</Alert>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {tenants.map((t) => <Chip key={t} label={t} />)}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.dark" }}>Notificações</Typography>
          <Stack spacing={2}>
            <TextField select label="Canal preferido" defaultValue="inapp" sx={{ maxWidth: 260 }}>
              <MenuItem value="inapp">In-app</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
            </TextField>
            <Box>
              <Typography variant="caption" color="text.secondary">Tipos que quero receber:</Typography>
              <Stack>
                <FormControlLabel control={<Checkbox defaultChecked />} label="Erros e falhas" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Relatórios enviados" />
                <FormControlLabel control={<Checkbox />} label="Novos usuários no meu tenant" />
                <FormControlLabel control={<Checkbox />} label="Novidades da plataforma" />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

    </>
  );
}
